import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { useLanguage } from '@/context/LanguageContext';
import { fetchPartners } from '@/services/partnerService';
import { Project } from '@/types/partner';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, A11y, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { MapPin } from 'lucide-react';

type ProjectWithPartner = Project & { partner_id: string };

const Projects: React.FC = () => {
  const navigate = useNavigate();
  const { tl } = useLanguage();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('All');

  const { data: partners = [], isLoading } = useQuery({
    queryKey: ['partners'],
    queryFn: fetchPartners,
  });

  const allProjects: ProjectWithPartner[] = partners.reduce<ProjectWithPartner[]>(
    (acc, partner) => {
      if (partner.projects) {
        const projectsWithPartner = partner.projects.map((project) => ({
          ...project,
          partner_id: partner.id,
        }));
        return [...acc, ...projectsWithPartner];
      }
      return acc;
    },
    []
  );

  const filteredProjects = allProjects.filter((project) => {
    const matchesSearch = project.title.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory =
      selectedCategory === 'All' || project.project_category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const recentProjects = allProjects.slice(-10);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{tl('Projets', 'Projects', 'Progetti')} | Meubles Karim</title>
      </Helmet>

      <div
        className="relative w-full h-[30vh] bg-cover bg-center flex flex-col justify-center items-center text-white px-4"
        style={{ backgroundImage: 'url("/ProjetHero.png")' }}
      >
        <div className="absolute inset-0 bg-black/40" />
        <div className="relative z-10 max-w-4xl w-full flex flex-col items-center text-center">
          <h1 className="text-4xl md:text-5xl font-sans mb-6">
            Meubles Karim <br /> {tl('Projets', 'Projects', 'Progetti')}
          </h1>
        </div>
      </div>

      <div className="w-full bg-background">
        <Swiper
          modules={[Navigation, Pagination, A11y, Autoplay]}
          slidesPerView={1}
          spaceBetween={32}
          centeredSlides
          loop={recentProjects.length > 1}
          autoplay={{ delay: 2000, disableOnInteraction: false }}
          breakpoints={{
            768: { slidesPerView: 1.5, spaceBetween: 32 },
            1024: { slidesPerView: 2.5, spaceBetween: 32 },
            1280: { slidesPerView: 3, spaceBetween: 32 },
          }}
          className="project-hero-swiper py-12 md:py-16"
        >
          {recentProjects.map((project) => (
            <SwiperSlide key={project.id}>
              {({ isActive }) => (
                <div
                  className={`relative group cursor-pointer h-[50vw] max-h-[520px] rounded-lg overflow-hidden shadow-lg transition-all duration-500 ${
                    isActive ? 'z-20 scale-105' : 'z-10 scale-95'
                  }`}
                  onClick={() => navigate(`/partners/${project.partner_id}?project=${project.id}`)}
                  style={{ minHeight: 320 }}
                >
                  <img
                    src={project.images[0]}
                    alt={project.title}
                    className="w-full h-full object-cover transition-transform duration-500"
                    loading="lazy"
                  />
                  {project.location && (
                    <div className="absolute top-4 left-4 z-20">
                      <span className="inline-flex items-center gap-1.5 bg-white/90 text-foreground text-xs font-bold px-3 py-1 rounded-full shadow">
                        <MapPin className="w-3.5 h-3.5" />
                        {project.location}
                      </span>
                    </div>
                  )}
                  {!isActive && (
                    <div className="absolute inset-0 bg-black/60 transition-opacity duration-300 z-10" />
                  )}
                  <div className="absolute bottom-8 left-0 w-full z-20 flex justify-center">
                    <h2
                      className="text-2xl md:text-3xl font-sans text-white text-center drop-shadow-lg px-4"
                      style={{ textShadow: '0 2px 8px rgba(0,0,0,0.5)' }}
                    >
                      {project.title}
                    </h2>
                  </div>
                </div>
              )}
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      <div className="container mx-auto px-4 py-16">
        <div className="w-full flex justify-center text-foreground">
          <div className="w-full max-w-3xl flex flex-col md:flex-row gap-4 mb-12 px-4">
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-full md:w-[200px]">
                <SelectValue placeholder={tl('Catégorie', 'Category', 'Categoria')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="All">{tl('Tous', 'All', 'Tutti')}</SelectItem>
                <SelectItem value="Hotels">{tl('Hôtels', 'Hotels', 'Hotel')}</SelectItem>
                <SelectItem value="Villas">{tl('Villas', 'Villas', 'Ville')}</SelectItem>
                <SelectItem value="Restaurants">
                  {tl('Restaurants', 'Restaurants', 'Ristoranti')}
                </SelectItem>
                <SelectItem value="Coffee shops">
                  {tl('Cafés', 'Coffee shops', 'Caffè')}
                </SelectItem>
              </SelectContent>
            </Select>

            <Input
              type="text"
              placeholder={tl(
                'Rechercher un projet...',
                'Search projects...',
                'Cerca un progetto...'
              )}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {filteredProjects.map((project) => (
            <motion.div
              key={project.id}
              className="group cursor-pointer"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              onClick={() => navigate(`/partners/${project.partner_id}?project=${project.id}`)}
            >
              <div className="relative aspect-[4/3] overflow-hidden rounded-lg">
                <img
                  src={project.images[0]}
                  alt={project.title}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                  loading="lazy"
                />
                {project.location && (
                  <div className="absolute top-3 left-3 z-10">
                    <span className="inline-flex items-center gap-1.5 bg-white/90 text-foreground text-xs font-medium px-2.5 py-1 rounded-full shadow">
                      <MapPin className="w-3.5 h-3.5" />
                      {project.location}
                    </span>
                  </div>
                )}
                <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </div>
              <h3 className="mt-4 text-foreground text-xl font-sans text-center">{project.title}</h3>
            </motion.div>
          ))}
        </div>

        {filteredProjects.length === 0 && (
          <div className="text-center text-muted-foreground mt-8">
            {tl('Aucun projet trouvé', 'No projects found', 'Nessun progetto trovato')}
          </div>
        )}
      </div>
    </div>
  );
};

export default Projects;
